import {createReducer, on} from '@ngrx/store';
import {CartItem} from '../../data/model';
import * as cartActions from './cart.actions';
import {TaxEstimation} from '@sportngin/se-commerce-sdk';

export const cartFeatureKey = 'cart';

export interface CartState {
  items: CartItem[];
  enableCart: boolean;
  showCart: boolean;
  loading: boolean;
  failedItems: CartItem[];
  tax?: TaxEstimation;
}

export const initialState: CartState = {
  items: [],
  enableCart: true,
  showCart: false,
  loading: false,
  failedItems: [],
  tax: undefined,
};

export const reducer = createReducer(
  initialState,

  on(cartActions.setEnableCartSlideOut, (state, action) => ({
    ...state,
    enableCart: action.enabled,
  })),
  on(cartActions.showCartSlideOut, state => ({
    ...state,
    showCart: true,
  })),
  on(cartActions.loadCartFailure, state => ({...state, loading: false})),
  on(cartActions.hideCartSlideOut, state => ({...state, showCart: false})),
  on(cartActions.loadCartSuccess, (state, action) => ({
    ...state,
    items: action.cartItems ? action.cartItems.map(i => new CartItem(i)) : [],
    loading: false,
  })),
  on(cartActions.addItemToCartSuccess, (state, action) => {
    const newItem = new CartItem(action.item);
    const items = [...state.items];
    const index = state.items.findIndex(item => item.equals(newItem));
    if (index >= 0) {
      items[index] = newItem;
    } else {
      items.push(newItem);
    }
    const stillFailing =
      state.failedItems?.filter(item => item.id !== action.item.id) ?? [];
    return {
      ...state,
      loading: false,
      items: items,
      failedItems: [...stillFailing],
    };
  }),
  on(
    cartActions.addItemToCartSuccessThenContinue,
    cartActions.addItemToCartSuccessThenComplete,
    cartActions.addItemToCartSuccessAndContinueToShipping,
    (state, action) => {
      const newItem = new CartItem(action.item);
      const items = [...state.items];
      const index = state.items.findIndex(item => item.equals(newItem));
      if (index >= 0) {
        items[index] = newItem;
      } else {
        items.push(newItem);
      }
      const stillFailing =
        state.failedItems?.filter(item => item.id !== action.item.id) ?? [];
      return {
        ...state,
        loading: false,
        items: items,
        failedItems: [...stillFailing],
      };
    },
  ),
  on(
    cartActions.removeItemFromCartSuccess,
    cartActions.removeItemFromCartSuccessThenComplete,
    cartActions.removeItemFromCartThenComplete,
    cartActions.removeItemFromCartSuccessThenContinue,
    cartActions.removeItemFromCartSuccessThenShipping,
    (state, action) => {
      const removedItem = new CartItem(action.item);
      const cartItems = [...state.items];
      const index = cartItems.findIndex(item => item.equals(removedItem));
      if (index >= 0) {
        cartItems.splice(index, 1);
      }
      return {...state, loading: false, items: cartItems};
    },
  ),
  on(cartActions.updateCartItemSuccess, (state, action) => {
    const updatedItem = new CartItem(action.item);
    const index = state.items.findIndex(item => item.equals(updatedItem));
    const items = [...state.items];
    if (index >= 0) {
      items[index] = updatedItem;
    } else {
      items.push(updatedItem);
    }
    return {...state, loading: false, items: items};
  }),
  on(cartActions.updateBundleItemsQuantitySuccess, (state, action) => {
    const items = [...state.items];
    for (const updatedItem of action.items) {
      const cartItem = new CartItem(updatedItem);
      const index = items.findIndex(item => item.equals(cartItem));
      if (index >= 0) {
        items[index] = cartItem;
      } else {
        items.push(cartItem);
      }
    }
    return {...state, items};
  }),
  on(
    cartActions.loadCart,
    cartActions.addClareBundleToCart,
    cartActions.removeBundleItemsFromCart,
    cartActions.addItemToCart,
    cartActions.addItemToCartAndContinue,
    cartActions.addItemToCartAndComplete,
    cartActions.addItemToCartAndContinueToShipping,
    cartActions.removeItemFromCartThenShipping,
    cartActions.removeItemFromCart,
    cartActions.updateCartItem,
    cartActions.addOrUpdateCartItem,
    cartActions.removeItemFromCartThenContinue,
    cartActions.removeCurrentAddonFromCartThenShipping,
    state => {
      return {...state, loading: true};
    },
  ),
  on(cartActions.cartActionFailure, state => {
    return {...state, loading: false};
  }),
  on(cartActions.cartSubmitFailure, state => {
    return {...state, loading: false};
  }),
  on(cartActions.addItemToCartFailure, (state, action) => {
    const isNewFailedItem: boolean = state.failedItems.find(
      item => item.id === action.item.id,
    )
      ? false
      : true;
    if (!isNewFailedItem) return state;
    return {
      ...state,
      loading: false,
      failedItems: [...state.failedItems, action.item],
    };
  }),
  on(cartActions.retryAddCartItems, state => {
    return {...state, loading: true};
  }),
  on(cartActions.retryCartItemSuccess, (state, action) => {
    const stillFailing =
      state.failedItems?.filter(
        item => item.productId !== action.item.productId,
      ) ?? [];
    const newItem = new CartItem(action.item);
    const items = [...state.items];
    const index = state.items.findIndex(item => item.equals(newItem));
    if (index >= 0) {
      items[index] = newItem;
    } else {
      items.push(newItem);
    }
    const loading = !doneRetrying(state, action.item);
    return {...state, items, failedItems: [...stillFailing], loading};
  }),
  on(cartActions.retryCartItemFailure, (state, action) => {
    const loading = !doneRetrying(state, action.item);
    return {...state, loading};
  }),
  on(cartActions.getCartTaxSuccess, (state, action) => {
    return {...state, tax: action.tax};
  }),
  on(cartActions.getCartTaxFailure, (state, action) => {
    return {...state, error: action.error};
  }),
  on(cartActions.emptyCartSuccess, state => {
    return {...state, items: [], failedItems: []};
  }),
  on(cartActions.clearFailedItems, state => {
    return {...state, failedItems: []};
  }),
);

const doneRetrying = (state: CartState, currentItem: CartItem): boolean => {
  const lastIndex = state.failedItems.length - 1;
  const lastItem = lastIndex > -1 ? state.failedItems[lastIndex] : undefined;
  return lastItem?.productId === currentItem.productId;
};

export const getCartItems = (state: CartState): CartItem[] => state.items;
export const getShowCart = (state: CartState): boolean => state.showCart;
export const getEnableCart = (state: CartState): boolean => state.enableCart;
export const getLoadingStatus = (state: CartState): boolean => state.loading;
export const getFailedItems = (state: CartState): CartItem[] =>
  state.failedItems;
export const getTax = (state: CartState): TaxEstimation | undefined =>
  state.tax;
